import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { apiPost } from "@/lib/api-client";
import { useVenueSwitcher } from "@/contexts/venue-switcher-context";

interface User {
  id: string;
  email: string;
  venueId: string | null;
  role: string;
}

interface Tokens {
  token: string;
  refreshToken: string;
}

interface AuthContextType {
  user: User | null;
  tokens: Tokens | null;
  isLoading: boolean;
  effectiveVenueId: string | null;
  login: (email: string) => Promise<void>;
  logout: () => void;
  validateSession: () => Promise<boolean>;
  verifyOtp: (email: string, code: string) => Promise<User>;
  updateVenueId: (venueId: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Dev bypass: seed a mock admin session when VITE_DEV_BYPASS=true
const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS === "true";
const DEV_ROLE = (import.meta.env.VITE_DEV_ROLE as string) ?? "admin";
const DEV_USER: User = {
  id: "dev-user-001",
  email: DEV_ROLE === "admin" ? "dev@rollerlabs.ai" : "dev-customer@example.com",
  venueId: DEV_ROLE === "admin" ? null : "dev-venue-001",
  role: DEV_ROLE,
};
const DEV_TOKENS: Tokens = {
  token: "dev-bypass-token",
  refreshToken: "dev-bypass-refresh",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(DEV_BYPASS ? DEV_USER : null);
  const [tokens, setTokens] = useState<Tokens | null>(DEV_BYPASS ? DEV_TOKENS : null);
  const [isLoading, setIsLoading] = useState(false);
  const { impersonatingVenueId } = useVenueSwitcher();

  // Restore auth state from localStorage on mount (skipped in dev bypass mode)
  useEffect(() => {
    if (DEV_BYPASS) return;
    const stored = localStorage.getItem("auth");
    if (stored) {
      try {
        const { user: storedUser, tokens: storedTokens } = JSON.parse(stored);
        setUser(storedUser);
        setTokens(storedTokens);
      } catch {
        localStorage.removeItem("auth");
      }
    }
  }, []);

  const login = useCallback(async (email: string) => {
    setIsLoading(true);
    try {
      const response = await apiPost<{ success: boolean; data: { message: string } }>(
        "/auth/otp",
        { email }
      );
      if (!response.success) {
        throw new Error("Failed to send OTP");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const validateSession = useCallback(async (): Promise<boolean> => {
    const stored = localStorage.getItem("auth");
    if (!stored) return false;
    try {
      const { tokens: storedTokens, user: storedUser } = JSON.parse(stored);
      if (!storedTokens?.refreshToken) return false;
      const response = await apiPost<{
        success: boolean;
        data: { token: string; refreshToken: string; user: User };
      }>("/auth/refresh", { refreshToken: storedTokens.refreshToken });
      if (!response.success) {
        setUser(null);
        setTokens(null);
        localStorage.removeItem("auth");
        return false;
      }
      const { token, refreshToken } = response.data;
      const newTokens = { token, refreshToken };
      setUser(storedUser);
      setTokens(newTokens);
      localStorage.setItem("auth", JSON.stringify({ user: storedUser, tokens: newTokens }));
      return true;
    } catch {
      setUser(null);
      setTokens(null);
      localStorage.removeItem("auth");
      return false;
    }
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string): Promise<User> => {
    setIsLoading(true);
    try {
      const response = await apiPost<{
        success: boolean;
        data: { token: string; refreshToken: string; user: User };
      }>("/auth/verify-otp", { email, code });

      if (!response.success) {
        throw new Error("Invalid OTP");
      }

      const { user: authUser, token, refreshToken } = response.data;
      const newTokens = { token, refreshToken };

      setUser(authUser);
      setTokens(newTokens);
      localStorage.setItem("auth", JSON.stringify({ user: authUser, tokens: newTokens }));
      return authUser;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setTokens(null);
    localStorage.removeItem("auth");
  }, []);

  const updateVenueId = useCallback((venueId: string) => {
    if (user) {
      const updatedUser = { ...user, venueId };
      setUser(updatedUser);
      const stored = localStorage.getItem("auth");
      if (stored) {
        const parsed = JSON.parse(stored);
        localStorage.setItem("auth", JSON.stringify({ ...parsed, user: updatedUser }));
      }
    }
  }, [user]);

  const effectiveVenueId = impersonatingVenueId ?? user?.venueId ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isLoading,
        effectiveVenueId,
        login,
        verifyOtp,
        logout,
        updateVenueId,
        validateSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
